import React, { useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";

const PRIMARY_COLOR = "rgb(53, 96, 90)";
const SECONDARY_COLOR = "rgb(1, 165, 156)";
const LIGHT_COLOR = "rgba(53, 96, 90, 0.08)";
const SUCCESS_COLOR = "#28a745";
const WARNING_COLOR = "#ffc107";
const INFO_COLOR = "#17a2b8";


const ResumenComprasSunat = ({ compras = [] }) => {
    const [expandirProveedores, setExpandirProveedores] = useState(false);

    const formatearMoneda = useCallback((valor, moneda) => {
        const safeCurrency = moneda === "USD" ? "USD" : "PEN";
        const num = Number(valor || 0);

        return new Intl.NumberFormat("es-PE", {
            style: "currency",
            currency: safeCurrency,
            minimumFractionDigits: 2,
        }).format(num);
    }, []);

    const resumen = useMemo(() => {
        const totalRegistros = compras.length;
        
        const procesadas = compras.filter(c => c.yaRegistrado === true).length;
        const pendientes = totalRegistros - procesadas;
        const porcentajeProcesado = totalRegistros > 0 ? Math.round((procesadas / totalRegistros) * 100) : 0;

        const totalPEN = compras
            .filter(item => item.moneda === "PEN")
            .reduce((acc, item) => acc + Number(item.total || 0), 0);

        const totalUSD = compras
            .filter(item => item.moneda === "USD")
            .reduce((acc, item) => acc + Number(item.total || 0), 0);

        const tipoDocMap = {};
        compras.forEach(c => {
            const tipo = c.tipo_doc_descripcion || "Desconocido";
            tipoDocMap[tipo] = (tipoDocMap[tipo] || 0) + 1;
        });

        const docMasFrecuente = Object.entries(tipoDocMap).length > 0 
            ? Object.entries(tipoDocMap).sort((a, b) => b[1] - a[1])[0]
            : null;

        const proveedorMap = {};
        compras.forEach(c => {
            const proveedor = c.razon_social || "Desconocido";
            if (!proveedorMap[proveedor]) {
                proveedorMap[proveedor] = {
                    nombre: proveedor,
                    ruc: c.ruc_proveedor || "",
                    cantidad: 0,
                    monto: 0,
                    monedas: {}
                };
            }
            proveedorMap[proveedor].cantidad += 1;
            proveedorMap[proveedor].monto += Number(c.total || 0);
            proveedorMap[proveedor].monedas[c.moneda] = (proveedorMap[proveedor].monedas[c.moneda] || 0) + Number(c.total || 0);
        });

        const top5Proveedores = Object.values(proveedorMap)
            .sort((a, b) => b.monto - a.monto)
            .slice(0, 5);

        return {
            totalRegistros,
            procesadas,
            pendientes,
            porcentajeProcesado,
            totalPEN,
            totalUSD,
            promedio: totalRegistros > 0 ? (totalPEN + totalUSD) / totalRegistros : 0,
            tipoDocMap,
            docMasFrecuente,
            totalMoneda: totalPEN + totalUSD,
            top5Proveedores
        };
    }, [compras]);

    const renderCardMetrica = (titulo, icono, valor, subtitulo, color) => {
        return (
            <div className="col-md-3 mb-3">
                <div 
                    className="card h-100 border-0 shadow-sm"
                    style={{ 
                        borderTop: `4px solid ${color || SECONDARY_COLOR}`,
                        backgroundColor: 'white'
                    }}
                >
                    <div className="card-header text-white" style={{backgroundColor: PRIMARY_COLOR}}>
                        <h6 className="mb-0">
                            <i className={`fas ${icono} mr-2`}></i>
                            {titulo}
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="h4 mb-0 font-weight-bold" style={{ color: PRIMARY_COLOR }}>
                            {valor}
                        </div>
                        <small className="text-muted">{subtitulo}</small>
                    </div>
                </div>
            </div>
        );
    };

    const renderCardProcesamiento = () => {
        return (
            <div className="col-lg-6 mb-3">
                <div 
                    className="card h-100 border-0 shadow-sm"
                    style={{
                        borderTop: `4px solid ${WARNING_COLOR}`,
                        backgroundColor: 'white'
                    }}
                >
                    <div className="card-header text-white" style={{backgroundColor: PRIMARY_COLOR}}>
                        <h6 className="mb-0">
                            <i className="fas fa-tasks mr-2"></i>
                            Estado de Procesamiento
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="font-weight-bold" style={{ color: PRIMARY_COLOR }}>
                                    Procesadas: {resumen.porcentajeProcesado}%
                                </span>
                            </div>
                            <div 
                                className="progress" 
                                style={{ height: "10px", borderRadius: "4px", backgroundColor: "#e9ecef" }}
                            >
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${resumen.porcentajeProcesado}%`,
                                        backgroundColor: SUCCESS_COLOR,
                                        transition: "width 0.3s ease"
                                    }}
                                ></div>
                            </div>
                        </div>

                        <div className="row text-center">
                            <div className="col-6">
                                <div 
                                    style={{
                                        backgroundColor: "rgba(40, 167, 69, 0.1)",
                                        padding: "12px",
                                        borderRadius: "6px"
                                    }}
                                >
                                    <p style={{ margin: 0, fontSize: "1.3rem", fontWeight: "700", color: SUCCESS_COLOR }}>
                                        {resumen.procesadas}
                                    </p>
                                    <small className="text-muted">Procesadas</small>
                                </div>
                            </div>
                            <div className="col-6">
                                <div 
                                    style={{
                                        backgroundColor: "rgba(255, 193, 7, 0.1)",
                                        padding: "12px",
                                        borderRadius: "6px"
                                    }}
                                >
                                    <p style={{ margin: 0, fontSize: "1.3rem", fontWeight: "700", color: WARNING_COLOR }}>
                                        {resumen.pendientes}
                                    </p>
                                    <small className="text-muted">Pendientes</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderCardMonedas = () => {
        return (
            <div className="col-lg-6 mb-3">
                <div 
                    className="card h-100 border-0 shadow-sm"
                    style={{
                        borderTop: `4px solid ${INFO_COLOR}`,
                        backgroundColor: 'white'
                    }}
                >
                    <div className="card-header text-white" style={{backgroundColor: PRIMARY_COLOR}}>
                        <h6 className="mb-0">
                            <i className="fas fa-coins mr-2"></i>
                            Desglose por Moneda
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="font-weight-bold">
                                    <i className="fas fa-money-bill mr-2" style={{ color: "#FF6B6B" }}></i>PEN
                                </span>
                                <span style={{ fontSize: "1.05rem", fontWeight: "700", color: "#FF6B6B" }}>
                                    {formatearMoneda(resumen.totalPEN, "PEN")}
                                </span>
                            </div>
                        </div>

                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="font-weight-bold">
                                    <i className="fas fa-money-bill-wave mr-2" style={{ color: "#4ECDC4" }}></i>USD
                                </span>
                                <span style={{ fontSize: "1.05rem", fontWeight: "700", color: "#4ECDC4" }}>
                                    {formatearMoneda(resumen.totalUSD, "USD")}
                                </span>
                            </div>
                        </div>

                        <div 
                            style={{
                                borderTop: "1px solid #e9ecef",
                                marginTop: "12px",
                                paddingTop: "12px"
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="font-weight-bold">Total General</span>
                                <span style={{ fontSize: "1.1rem", fontWeight: "700", color: PRIMARY_COLOR }}>
                                    {formatearMoneda(resumen.totalMoneda, "PEN")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDesgloseTipoDoc = () => {
        if (Object.keys(resumen.tipoDocMap).length === 0) {
            return null;
        }

        const items = Object.entries(resumen.tipoDocMap)
            .sort((a, b) => b[1] - a[1])
            .map(([tipo, cantidad], index) => {
                const porcentaje = (cantidad / resumen.totalRegistros) * 100;
                const colores = ["#FF6B6B", "#4ECDC4", "#FFE66D", "#95E1D3", "#C7CEEA"];
                const color = colores[index % colores.length];

                return (
                    <div key={tipo} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                            <span className="font-weight-bold">
                                <i className="fas fa-file mr-1" style={{ color }}></i>
                                {tipo}
                            </span>
                            <span style={{ fontWeight: "700", color }}>
                                {cantidad} ({Math.round(porcentaje)}%)
                            </span>
                        </div>
                        <div 
                            className="progress" 
                            style={{ height: "6px", borderRadius: "3px", backgroundColor: "#e9ecef" }}
                        >
                            <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                    width: `${porcentaje}%`,
                                    backgroundColor: color,
                                    transition: "width 0.3s ease"
                                }}
                            ></div>
                        </div>
                    </div>
                );
            });

        return (
            <div className="card border-0 shadow-sm mb-3" style={{ 
                borderTop: `4px solid ${INFO_COLOR}`,
                backgroundColor: 'white'
            }}>
                <div className="card-header text-white" style={{backgroundColor: PRIMARY_COLOR}}>
                    <h6 className="mb-0">
                        <i className="fas fa-filter mr-2"></i>
                        Desglose por Tipo de Documento
                    </h6>
                </div>
                <div className="card-body">
                    {items}
                </div>
            </div>
        );
    };

    const renderTop5Proveedores = () => {
        if (resumen.top5Proveedores.length === 0) {
            return null;
        }

        return (
            <div className="card border-0 shadow-sm mb-3" style={{ 
                borderTop: `4px solid ${SECONDARY_COLOR}`,
                backgroundColor: 'white'
            }}>
                <div 
                    className="card-header text-white" 
                    style={{
                        backgroundColor: PRIMARY_COLOR,
                        cursor: "pointer"
                    }}
                    onClick={() => setExpandirProveedores(!expandirProveedores)}
                >
                    <h6 className="mb-0 d-flex justify-content-between align-items-center">
                        <span>
                            <i className="fas fa-box-open mr-2"></i>
                            Top 5 Proveedores
                        </span>
                        <i className={`fas fa-chevron-${expandirProveedores ? 'up' : 'down'}`}></i>
                    </h6>
                </div>
                {expandirProveedores && (
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-sm mb-0">
                                <thead style={{ backgroundColor: LIGHT_COLOR }}>
                                    <tr>
                                        <th style={{ color: PRIMARY_COLOR, fontWeight: "700" }}>#</th>
                                        <th style={{ color: PRIMARY_COLOR, fontWeight: "700" }}>Proveedor</th>
                                        <th style={{ color: PRIMARY_COLOR, fontWeight: "700" }}>RUC</th>
                                        <th style={{ color: PRIMARY_COLOR, fontWeight: "700", textAlign: "center" }}>Qty</th>
                                        <th style={{ color: PRIMARY_COLOR, fontWeight: "700", textAlign: "right" }}>Monto Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resumen.top5Proveedores.map((proveedor, idx) => (
                                        <tr key={idx} style={{ borderBottom: "1px solid #e9ecef" }}>
                                            <td>
                                                <span 
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: "24px",
                                                        height: "24px",
                                                        borderRadius: "50%",
                                                        backgroundColor: SECONDARY_COLOR,
                                                        color: "white",
                                                        fontWeight: "700",
                                                        fontSize: "0.8rem"
                                                    }}
                                                >
                                                    {idx + 1}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: "600", color: PRIMARY_COLOR }}>
                                                    {proveedor.nombre}
                                                </div>
                                            </td>
                                            <td>
                                                <small className="text-muted">{proveedor.ruc}</small>
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <span 
                                                    style={{
                                                        display: "inline-block",
                                                        backgroundColor: "rgba(1, 165, 156, 0.1)",
                                                        padding: "4px 8px",
                                                        borderRadius: "4px",
                                                        fontWeight: "700",
                                                        color: SECONDARY_COLOR
                                                    }}
                                                >
                                                    {proveedor.cantidad}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: "right", fontWeight: "700", color: PRIMARY_COLOR }}>
                                                {formatearMoneda(proveedor.monto, "PEN")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (compras.length === 0) {
        return null;
    }

    return (
        <div className="animated fadeIn">
            <div className="row mb-4">
                {renderCardMetrica("Total de Compras", "fa-receipt", resumen.totalRegistros, "Registros SUNAT", SECONDARY_COLOR)}
                {renderCardMetrica("Promedio por Compra", "fa-calculator", formatearMoneda(resumen.promedio, "PEN"), "Monto promedio", WARNING_COLOR)}
                {renderCardMetrica("Documento Frecuente", "fa-chart-pie", resumen.docMasFrecuente ? `${resumen.docMasFrecuente[0]} (${resumen.docMasFrecuente[1]})` : "N/A", "Tipo más común", INFO_COLOR)}
                {renderCardMetrica("Total General", "fa-coins", formatearMoneda(resumen.totalMoneda, "PEN"), "Suma total", SECONDARY_COLOR)}
            </div>

            <div className="row mb-4">
                {renderCardProcesamiento()}
                {renderCardMonedas()}
            </div>

            {renderDesgloseTipoDoc()}
            
            {renderTop5Proveedores()}
        </div>
    );
};

ResumenComprasSunat.propTypes = {
    compras: PropTypes.arrayOf(
        PropTypes.shape({
            moneda: PropTypes.string,
            total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            tipo_doc_descripcion: PropTypes.string,
            yaRegistrado: PropTypes.bool,
            razon_social: PropTypes.string,
            ruc_proveedor: PropTypes.string
        })
    )
};

ResumenComprasSunat.defaultProps = {
    compras: []
};

export default ResumenComprasSunat;
